import type { ApolloError } from '@apollo/client';
import {
  Badge,
  Spinner,
  getIcon,
  Button,
  Icon,
} from '@quality24/design-system';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import classNames from 'classnames';

import { BadgeComponentData } from '../../../../@types';
import { formatValue, parseDataValue } from '../../../../utils/dataUtils';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  data: Record<string, Record<string, unknown>>;
  error?: ApolloError;
  refetch: () => void;
  config: Omit<BadgeComponentData, 'type'>;
}

/**
 * <BadgeContainer> page
 */
export const BadgeContainer: React.FunctionComponent<Props> = ({
  className,
  data,
  loading,
  error,
  refetch,
  config: {
    className: configClassName,
    rootKey,
    label,
    value,
    icon,
    dataType,
    formatter,
    mapping,
  },
  ...htmlElementProps
}) => {
  const parsedValue = React.useMemo(
    () =>
      formatValue(
        data
          ? (parseDataValue(data[rootKey], value, dataType || 'string') as
              | string
              | number)
          : '',
        formatter,
        mapping,
      ),
    [data, rootKey, value, dataType, formatter, mapping],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('BadgeContainer', {
      originalData: data,
      parsedData: parsedValue,
    });
  }

  return (
    <div
      className={classNames(className, configClassName)}
      {...htmlElementProps}
    >
      <Badge icon={icon ? getIcon(icon) : undefined}>
        <span>{label} &nbsp;</span>

        {/* On loading */}
        {loading && (
          <Spinner type="light" size="xs" data-testid="badge-spinner" />
        )}
        {/* On error */}
        {!loading && error && (
          <Button className="p-0" variant="link" size="sm" onClick={refetch}>
            <Icon className="m-0" icon={faRefresh} />
          </Button>
        )}

        {!loading && !error && <strong>{parsedValue}</strong>}
      </Badge>
    </div>
  );
};

export default BadgeContainer;
